import { useState, type CSSProperties } from 'react'
import { cn } from '@/lib/utils'
import { useActiveBrand } from './useBrand'

// One loop of the YLLW mark, as in brand-loader.css.
const LOOP_MS = 7200

/**
 * The logo, wherever the product signs its own name. YLLW ships as an asset;
 * the Solvigo mark is inline so it can take its colours from the brand tokens
 * rather than needing a second file per palette.
 *
 * `animated` is the loading version (BrandLoader): for YLLW the mark alone,
 * redrawn as four blocks that move the way yllw.com's logo does; for Solvigo
 * its own mark, breathing. Neither sits on a tile: the ink blocks read on the
 * page background and on a white card alike. brand-loader.css has the motion
 * and drops it under reduced motion, leaving the logo at rest.
 */
export function BrandMark({ className, animated = false }: {
  className?: string
  animated?: boolean
}) {
  const brand = useActiveBrand()
  // Every animated mark runs on the page clock, so the loader that takes over
  // from the boot loader carries on mid-loop instead of restarting.
  const [phase] = useState(() => `${-(performance.now() % LOOP_MS)}ms`)

  if (brand.key === 'solvigo') {
    // Two overlapping quadrilaterals with the intersection knocked to ink, so
    // the mark reads the same on paper as it does on a dark surface.
    // --brand-yellow, not --color-brand: that alias is resolved once at :root,
    // so it stays yellow under the Solvigo override (see shared/tokens.css).
    return (
      <svg
        viewBox="0 0 51.97 46"
        className={cn(animated ? 'h-20 w-auto brand-mark-breathe' : 'h-5 w-auto shrink-0', className)}
        role="img"
        aria-label="Solvigo"
      >
        <polygon fill="var(--color-brand-secondary)" points="12.7 25.17 12.7 46 51.97 46 51.97 6.86" />
        <polygon fill="var(--brand-yellow)" points="39.26 20.83 39.26 0 0 0 0 39.13" />
        <clipPath id="brandmark-clip">
          <polygon points="12.7 25.17 12.7 46 51.97 46 51.97 6.86" />
        </clipPath>
        <polygon
          fill="var(--color-ink-1000)"
          clipPath="url(#brandmark-clip)"
          points="39.26 20.83 39.26 0 0 0 0 39.13"
        />
      </svg>
    )
  }

  if (animated) {
    return (
      <span
        role="img"
        aria-label={brand.name}
        className={cn('yllw-mark w-20', className)}
        style={{ '--yllw-mark-phase': phase } as CSSProperties}
      >
        <span className="tl" />
        <span className="tr" />
        <span className="mid" />
        <span className="bl" />
      </span>
    )
  }

  return <img src="/brand/logo.svg" alt={brand.name} className={cn('h-5 w-auto shrink-0', className)} />
}
